import { useState, useEffect } from 'react';
import { adminApi } from '../../../utils/api';
import { Button, Spinner, Card } from '../shared';

const fmtCost = (n) => `$${(n || 0).toFixed(4)}`;
const fmt = (n) => {
    if (n == null) return '0';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return String(n);
};

export default function UserCostTab({ userId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('30d');

    useEffect(() => {
        loadData();
    }, [userId, period]);

    const loadData = async () => {
        setLoading(true);
        try {
            const result = await adminApi.getUserCostUsage(userId, { period });
            setData(result);
        } catch (err) {
            console.error('Failed to load cost data:', err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <Spinner text="Loading cost data..." center />;
    }

    if (!data) {
        return <p className="text-sm text-gray-400 text-center py-12">No data available</p>;
    }

    return (
        <div className="space-y-6 h-full overflow-y-auto">
            <div className="flex items-center gap-2">
                <Button variant={period === '7d' ? 'primary' : 'outline'} size="sm" onClick={() => setPeriod('7d')}>
                    7 days
                </Button>
                <Button variant={period === '30d' ? 'primary' : 'outline'} size="sm" onClick={() => setPeriod('30d')}>
                    30 days
                </Button>
                <Button variant={period === '90d' ? 'primary' : 'outline'} size="sm" onClick={() => setPeriod('90d')}>
                    90 days
                </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                    <p className="text-xs text-gray-500">Total Cost</p>
                    <p className="text-lg font-semibold text-gray-900 mt-1">{fmtCost(data.summary?.total_cost)}</p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                    <p className="text-xs text-gray-500">Avg per Day</p>
                    <p className="text-lg font-semibold text-gray-900 mt-1">{fmtCost(data.summary?.avg_daily_cost)}</p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                    <p className="text-xs text-gray-500">Total Requests</p>
                    <p className="text-lg font-semibold text-gray-900 mt-1">{fmt(data.summary?.total_requests)}</p>
                </div>
            </div>

            <Card title="Usage by Model">
                {data.model_breakdown && data.model_breakdown.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-200">
                                    <th className="text-left py-2 text-xs font-medium text-gray-500">Model</th>
                                    <th className="text-right py-2 text-xs font-medium text-gray-500">Requests</th>
                                    <th className="text-right py-2 text-xs font-medium text-gray-500">Tokens</th>
                                    <th className="text-right py-2 text-xs font-medium text-gray-500">Cost</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {data.model_breakdown.map((m, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50">
                                        <td className="py-2 text-gray-700">{m.model_name}</td>
                                        <td className="py-2 text-right text-gray-700">{m.request_count}</td>
                                        <td className="py-2 text-right text-gray-700">{fmt(m.total_tokens)}</td>
                                        <td className="py-2 text-right font-mono text-gray-700">{fmtCost(m.total_cost)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="text-sm text-gray-400 text-center py-4">No usage data</p>
                )}
            </Card>
        </div>
    );
}
